import { useEffect, useRef, createElement } from 'react';
import { toast } from 'sonner';
import { communityTrpc } from '@org/clients/community';
import { graphTrpc } from '@org/clients/graph';
import { MessageToast } from '@/components/notification-toast';

type ConversationRow = {
  message_id: string;
  message: string | null;
  created_at: string | Date;
  read_at: string | Date | null;
  sender_id: string;
  partner_id: string;
  partner_first_name: string | null;
  partner_last_name: string | null;
  partner_display_name: string | null;
  partner_profile_image_url: string | null;
  unread_count: number | string;
};

export function useMessagesWatcher() {
  const utils = communityTrpc.useUtils();
  const { data: pref } = communityTrpc.preferences.getPopupPreferences.useQuery();
  const enabled = pref?.popupEnabled ?? true;

  const { data: myProfile } = graphTrpc.profiles.getMyProfile.useQuery();
  const myUserId = myProfile?.userId;

  const { data } = communityTrpc.messages.listConversations.useQuery(
    { limit: 20, offset: 0 },
    {
      enabled: enabled && Boolean(myUserId),
      refetchInterval: enabled ? 10_000 : false,
      refetchIntervalInBackground: false,
    }
  );

  const seenRef = useRef<Map<string, number> | null>(null);
  const initializedRef = useRef(false);

  const conversations = (data?.conversations as ConversationRow[] | undefined) ?? undefined;

  useEffect(() => {
    if (!enabled || !myUserId || !conversations) return;

    if (seenRef.current === null) seenRef.current = new Map();

    // First response: seed and don't pop.
    if (!initializedRef.current) {
      for (const c of conversations) {
        seenRef.current.set(c.partner_id, new Date(c.created_at).getTime());
      }
      initializedRef.current = true;
      return;
    }

    let firedAny = false;
    for (const c of conversations) {
      const ts = new Date(c.created_at).getTime();
      const prev = seenRef.current.get(c.partner_id) ?? 0;
      if (ts > prev && c.sender_id !== myUserId) {
        const actorName =
          c.partner_display_name ||
          [c.partner_first_name, c.partner_last_name].filter(Boolean).join(' ') ||
          'Someone';
        const preview = c.message ?? '';
        const partnerId = c.partner_id;
        const toastId = `msg-${partnerId}-${ts}`;
        toast.custom(
          () =>
            createElement(MessageToast, {
              partnerId,
              actorName,
              preview,
              avatarUrl: c.partner_profile_image_url,
              onClick: () => {
                toast.dismiss(toastId);
              },
            }),
          { duration: 10_000, id: toastId }
        );
        firedAny = true;
      }
      seenRef.current.set(c.partner_id, ts);
    }

    if (firedAny) {
      utils.messages.getUnreadCount.invalidate();
    }
  }, [conversations, enabled, myUserId, utils]);
}
